import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Fields to never show in diffs (noisy / internal)
const SKIP_FIELDS = new Set([
  'id', 'created_at', 'updated_at', 'is_active', 'user_agent',
]);

// Human-readable labels for common fields
const FIELD_LABELS = {
  status:            'Status',
  user_id:           'Assigned To',
  assigned_to:       'Assigned To',
  total:             'Total Amount',
  subtotal:          'Subtotal',
  discount_amount:   'Discount',
  freight_amount:    'Freight',
  po_number:         'PO Number',
  notes:             'Notes',
  shipping_address:  'Shipping Address',
  expected_delivery: 'Expected Delivery',
  currency:          'Currency',
  order_type:        'Order Type',
  quote_amount:      'Quote Amount',
  follow_up_date:    'Follow-up Date',
  quote_expiry_date: 'Quote Expiry',
  priority:          'Priority',
  budget:            'Budget',
  end_date:          'End Date',
  start_date:        'Start Date',
  title:             'Title',
  description:       'Description',
  outcome:           'Outcome',
  resolution_notes:  'Resolution Notes',
  resolved_at:       'Resolved At',
  purpose:           'Purpose',
  products_discussed:'Products Discussed',
  next_visit_date:   'Next Visit Date',
  leave_type:        'Leave Type',
  from_date:         'From Date',
  to_date:           'To Date',
  rejection_reason:  'Rejection Reason',
  approved_at:       'Approved At',
};

const ACTION_STYLE = {
  CREATE: { bg: '#dcfce7', color: '#15803d', label: 'Created'  },
  UPDATE: { bg: '#dbeafe', color: '#1d4ed8', label: 'Updated'  },
  DELETE: { bg: '#fee2e2', color: '#b91c1c', label: 'Deleted'  },
  FREEZE: { bg: '#fef9c3', color: '#a16207', label: 'Archived' },
  LOGIN:  { bg: '#f3f4f6', color: '#374151', label: 'Login'    },
};

function fmtVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function FieldDiff({ field, oldVal, newVal }) {
  const label = FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const isStatus = field === 'status' || field === 'priority' || field === 'outcome';
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.76rem', marginTop: 4 }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>{label}:</span>
      <span style={{ color: '#dc2626', textDecoration: 'line-through', fontFamily: isStatus ? undefined : 'monospace' }}>
        {fmtVal(oldVal)}
      </span>
      <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
      <span style={{ color: '#059669', fontWeight: 600, fontFamily: isStatus ? undefined : 'monospace' }}>
        {fmtVal(newVal)}
      </span>
    </div>
  );
}

function HistoryEntry({ log }) {
  const [expanded, setExpanded] = useState(false);
  const style = ACTION_STYLE[log.action] || ACTION_STYLE.UPDATE;

  let old_ = null, new_ = null;
  try { old_ = log.old_values ? JSON.parse(log.old_values) : null; } catch {}
  try { new_ = log.new_values ? JSON.parse(log.new_values) : null; } catch {}

  // Compute changed fields
  const diffs = [];
  if (log.action === 'UPDATE' && old_ && new_) {
    const allKeys = new Set([...Object.keys(old_), ...Object.keys(new_)]);
    for (const k of allKeys) {
      if (SKIP_FIELDS.has(k)) continue;
      const ov = old_[k], nv = new_[k];
      if (String(ov ?? '') !== String(nv ?? '')) {
        diffs.push({ field: k, oldVal: ov, newVal: nv });
      }
    }
  }

  const ts = new Date(log.created_at);
  const dateStr = ts.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = ts.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const hasDetails = diffs.length > 0 || ((log.action === 'CREATE' || log.action === 'DELETE') && new_);

  return (
    <div style={{ display: 'flex', gap: 10, paddingBottom: 12 }}>
      {/* Timeline dot */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 18 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: style.color, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, width: 2, background: 'var(--border)', marginTop: 4 }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: style.bg, color: style.color }}>
            {style.label}
          </span>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{log.user_name || 'System'}</span>
          {log.user_role && (
            <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
              {log.user_role}
            </span>
          )}
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{dateStr} {timeStr}</span>
        </div>

        {/* Diffs summary */}
        {log.action === 'UPDATE' && diffs.length > 0 && (
          <div style={{ marginTop: 5 }}>
            {/* Always show first 3 diffs inline */}
            {(expanded ? diffs : diffs.slice(0, 3)).map(d => (
              <FieldDiff key={d.field} field={d.field} oldVal={d.oldVal} newVal={d.newVal} />
            ))}
            {diffs.length > 3 && (
              <button onClick={() => setExpanded(e => !e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--primary)', padding: '2px 0', marginTop: 2 }}>
                {expanded ? 'Show less' : `+${diffs.length - 3} more changes`}
              </button>
            )}
          </div>
        )}

        {log.action === 'CREATE' && (
          <div style={{ marginTop: 3, fontSize: '0.76rem', color: 'var(--text-muted)' }}>Record created</div>
        )}
        {(log.action === 'DELETE' || log.action === 'FREEZE') && (
          <div style={{ marginTop: 3, fontSize: '0.76rem', color: '#dc2626' }}>Record archived/deleted</div>
        )}
      </div>
    </div>
  );
}

export default function EntityHistory({ entity, entityId }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [open,    setOpen]    = useState(false);
  const [fetched, setFetched] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true); setError(null);
    try {
      const { data: res } = await api.get(`/audit-logs/${entity}/${entityId}`);
      setLogs(res.data || []);
    } catch {
      setError('Could not load history.');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [entity, entityId]);

  useEffect(() => {
    if (open && !fetched && !loading) load();
  }, [open, fetched, loading, load]);

  // Reset when entity changes (different modal opened)
  useEffect(() => {
    setLogs([]); setOpen(false); setError(null); setFetched(false);
  }, [entityId]);

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'left' }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="var(--text-muted)" strokeWidth="2"
          style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6"/>
        </svg>
        <span style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          Activity History
        </span>
        {logs.length > 0 && (
          <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: 'var(--primary-lighter)', color: 'var(--primary)' }}>
            {logs.length}
          </span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 14 }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Loading history…
            </div>
          )}
          {error && (
            <div style={{ fontSize: '0.82rem', color: '#dc2626' }}>{error}</div>
          )}
          {!loading && !error && logs.length === 0 && (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>No history recorded yet.</div>
          )}
          {!loading && logs.length > 0 && (
            <div style={{ paddingLeft: 4 }}>
              {logs.map(log => <HistoryEntry key={log.id} log={log} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
